import { createSignal, createEffect, For, Show, Suspense } from "solid-js";
import { A, createAsync, useSearchParams, type RouteDefinition } from "@solidjs/router";
import { AppShell, PageHeader } from "~/components/layout";
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, Skeleton, EmptyState } from "~/components/ui";
import { NewTopicModal } from "~/components/modals";
import { getTopics } from "~/server/bff";
import { Plus, Bell, BellOff, TrendingUp, Tag, Users, Video } from "lucide-solid";

export const route = {
  preload: () => getTopics(),
} satisfies RouteDefinition;

export default function TopicsPage() {
  if (import.meta.env.DEV) {
    console.info("[debug] render TopicsPage");
  }
  const topics = createAsync(() => getTopics(), { deferStream: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNewTopicModal, setShowNewTopicModal] = createSignal(false);

  // Handle ?new=true from CommandPalette
  createEffect(() => {
    if (searchParams.new === "true") {
      setShowNewTopicModal(true);
      setSearchParams({ new: undefined });
    }
  });

  const openNewTopicModal = () => setShowNewTopicModal(true);

  return (
    <AppShell title="Topics">
      <div class="p-6">
        <PageHeader
          title="Topic Watchlists"
          description="Track coverage across your topics of interest"
          actions={
            <Button onClick={openNewTopicModal}>
              <Plus class="h-4 w-4 mr-1" />
              New Topic
            </Button>
          }
        />

        <Suspense fallback={<TopicsSkeleton />}>
          <Show when={topics()} fallback={<TopicsSkeleton />}>
            {(items) => (
              <Show
                when={items().length > 0}
                fallback={
                  <EmptyState
                    icon={<Tag class="h-12 w-12" />}
                    title="No topics yet"
                    description="Create your first topic watchlist to start tracking coverage"
                    actionLabel="Create Topic"
                    onAction={openNewTopicModal}
                  />
                }
              >
                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <For each={items()}>
                    {(topic) => (
                      <A href={`/explore/topics/${topic.id}`}>
                        <Card class="h-full hover:bg-accent/50 transition-colors cursor-pointer">
                          <CardHeader class="pb-2">
                            <div class="flex items-start justify-between">
                              <CardTitle class="text-lg">{topic.name}</CardTitle>
                              <Show when={topic.alertsEnabled} fallback={<BellOff class="h-4 w-4 text-muted-foreground" />}>
                                <Bell class="h-4 w-4 text-primary" />
                              </Show>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <Show when={topic.description}>
                              <p class="text-sm text-muted-foreground mb-3 line-clamp-2">
                                {topic.description}
                              </p>
                            </Show>

                            <div class="flex flex-wrap gap-1 mb-3">
                              <For each={topic.keywords.slice(0, 4)}>
                                {(keyword) => (
                                  <Badge variant="outline" class="text-xs">
                                    {keyword}
                                  </Badge>
                                )}
                              </For>
                              <Show when={topic.keywords.length > 4}>
                                <Badge variant="outline" class="text-xs">
                                  +{topic.keywords.length - 4}
                                </Badge>
                              </Show>
                            </div>

                            <div class="flex items-center gap-4 text-sm text-muted-foreground">
                              <span class="flex items-center gap-1">
                                <Video class="h-4 w-4" />
                                {topic.videoCount}
                              </span>
                              <span class="flex items-center gap-1">
                                <Users class="h-4 w-4" />
                                {topic.channelCount}
                              </span>
                              <Show when={topic.recentActivity > 0}>
                                <span class="flex items-center gap-1 text-green-600">
                                  <TrendingUp class="h-4 w-4" />
                                  +{topic.recentActivity}
                                </span>
                              </Show>
                            </div>
                          </CardContent>
                        </Card>
                      </A>
                    )}
                  </For>
                </div>
              </Show>
            )}
          </Show>
        </Suspense>
      </div>

      <Show when={showNewTopicModal()}>
        <Suspense>
          <NewTopicModal 
            open={showNewTopicModal()} 
            onOpenChange={setShowNewTopicModal} 
          />
        </Suspense>
      </Show>
    </AppShell>
  );
}

function TopicsSkeleton() {
  return (
    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <For each={[1, 2, 3, 4, 5, 6]}>
        {() => (
          <Card>
            <CardHeader class="pb-2">
              <Skeleton class="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton class="h-4 w-full mb-3" />
              <div class="flex gap-1 mb-3">
                <Skeleton class="h-5 w-16" />
                <Skeleton class="h-5 w-16" />
              </div>
              <Skeleton class="h-4 w-24" />
            </CardContent>
          </Card>
        )}
      </For>
    </div>
  );
}
